import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Calendar, TrendingUp } from 'lucide-react';
import { projectReportsService, type ProjectReportItem } from '../../services/projectReports';
import { toast } from 'sonner';
import { WeeklyStatusCard } from './WeeklyStatusCard';

interface ReportsSectionProps {
  projectId?: number;
  projectCode?: string;
}

export function ReportsSection({ projectId }: ReportsSectionProps) {
  const navigate = useNavigate();
  const [reportPeriodFrom, setReportPeriodFrom] = useState('2024-01-01');
  const [reportPeriodTo, setReportPeriodTo] = useState('2024-03-31');
  const [selectedReport, setSelectedReport] = useState<string | null>('project-status');
  const [selectedFormat, setSelectedFormat] = useState<'csv' | 'pdf' | 'excel' | 'ppt'>('csv');
  const [includeSections, setIncludeSections] = useState({
    executiveSummary: true,
    performanceMetrics: true,
    riskAnalysis: true,
    financialOverview: true,
    keyMilestones: true,
    recommendations: true,
  });
  const [reports, setReports] = useState<ProjectReportItem[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadReports = useCallback(() => {
    if (!projectId) return;
    setReportsLoading(true);
    projectReportsService
      .list(projectId)
      .then(setReports)
      .catch(() => toast.error('Failed to load reports'))
      .finally(() => setReportsLoading(false));
  }, [projectId]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleGenerate = async () => {
    if (!projectId || !selectedReport) return;
    const reportTypeLabel = reportTypes.find((r) => r.id === selectedReport);
    setGenerating(true);
    try {
      await projectReportsService.generate(projectId, {
        report_type: selectedReport,
        title: `${reportTypeLabel?.title ?? selectedReport} - ${reportPeriodFrom} to ${reportPeriodTo}`,
        description: reportTypeLabel?.description ?? undefined,
        status: 'completed',
        report_data: {
          period_from: reportPeriodFrom,
          period_to: reportPeriodTo,
          format: selectedFormat,
          include_sections: includeSections,
        },
      });
      toast.success('Report generated');
      loadReports();
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e ? (e as { response?: { data?: { detail?: string } } }).response?.data?.detail : null;
      toast.error(msg || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async (reportId: number) => {
    if (!projectId) return;
    try {
      const isCsv = selectedFormat === 'csv';
      if (isCsv) {
        const { blob, filename } = await projectReportsService.exportReportBlob(projectId, reportId, 'csv');
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const result = await projectReportsService.exportReport(projectId, reportId, 'json');
        const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${result.title || 'report'}-${result.report_id}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
      toast.success('Report downloaded');
    } catch {
      toast.error('Failed to download report');
    }
  };

  const reportTypes = [
    {
      id: 'portfolio',
      emoji: '📊',
      title: 'Portfolio Report',
      description: 'Executive summary of all programs and projects'
    },
    {
      id: 'project-status',
      emoji: '📈',
      title: 'Project Status Report',
      description: 'Detailed project performance metrics'
    },
    {
      id: 'risk',
      emoji: '⚠️',
      title: 'Risk Report',
      description: 'Portfolio risk analysis and trends'
    },
    {
      id: 'financial',
      emoji: '💰',
      title: 'Financial Report',
      description: 'Budget utilization and forecasts'
    },
    {
      id: 'resource',
      emoji: '👥',
      title: 'Resource Report',
      description: 'Capacity and allocation analysis'
    },
    {
      id: 'procurement',
      emoji: '📋',
      title: 'Procurement Report',
      description: 'Vendor and contract management'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      <WeeklyStatusCard projectId={projectId} />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#486581] via-[#5c748e] to-[#486581] rounded-xl p-8 text-white border-b-4 border-[#DAA520]">
        <div className="mb-6">
          <h1 className="text-3xl mb-2">Reports & Exports</h1>
          <p className="text-[#d4dce5]">Generate executive reports and export project data</p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-4 gap-6">
          <div className="bg-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">Available Templates</div>
            <div className="text-3xl font-medium text-neutral-950 mb-1">6</div>
            <div className="text-sm text-gray-600">
              Report types
            </div>
          </div>

          <div className="bg-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
                <Download className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">Generated Reports</div>
            <div className="text-3xl font-medium text-neutral-950 mb-1">{projectId ? reports.length : '—'}</div>
            <div className="text-sm text-green-600">
              {projectId ? 'From this project' : 'Select project'}
            </div>
          </div>

          <div className="bg-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">Scheduled Reports</div>
            <div className="text-3xl font-medium text-neutral-950 mb-1">12</div>
            <div className="text-sm text-purple-600">
              Recurring reports
            </div>
          </div>

          <div className="bg-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">Export Formats</div>
            <div className="text-3xl font-medium text-neutral-950 mb-1">3</div>
            <div className="text-sm text-orange-600">
              PDF, Excel, PPT
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-[1fr_320px] gap-6">
        {/* Left Column - Report Templates */}
        <div className="space-y-6">
          <h3 className="font-['Arial:Regular',sans-serif] text-[16px] leading-[24px] text-neutral-950">
            Generate New Report
          </h3>

          <div className="grid grid-cols-2 gap-4">
            {reportTypes.map((report, index) => (
              <button
                key={report.id}
                onClick={() => setSelectedReport(report.id)}
                className={`text-left p-4 rounded-[10px] transition-all ${
                  selectedReport === report.id
                    ? 'bg-indigo-50 border-[1.6px] border-[#4f39f6]'
                    : 'border-[1.6px] border-gray-200 hover:border-gray-300'
                } ${index >= 2 ? 'h-[167.15px]' : 'h-[183.137px]'}`}
              >
                <div className="flex flex-col gap-2">
                  <p className="text-[24px] leading-[32px]">{report.emoji}</p>
                  <h4 className="font-['Arial:Regular',sans-serif] text-[14px] leading-[20px] text-neutral-950">
                    {report.title}
                  </h4>
                  <p className="font-['Arial:Regular',sans-serif] text-[12px] leading-[16px] text-[#4a5565]">
                    {report.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right Column - Report Options */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6 h-fit">
          <h3 className="font-['Arial:Regular',sans-serif] text-[14px] leading-[20px] text-neutral-950">
            Report Options
          </h3>

          {/* Report Period */}
          <div className="space-y-2">
            <label className="font-['Arial:Regular',sans-serif] text-[14px] leading-[20px] text-neutral-950">
              Report Period
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="font-['Arial:Regular',sans-serif] text-[12px] leading-[16px] text-[#4a5565]">
                  From
                </label>
                <input
                  type="date"
                  value={reportPeriodFrom}
                  onChange={(e) => setReportPeriodFrom(e.target.value)}
                  className="w-full h-[37.6px] px-3 border border-[#d1d5dc] rounded-[10px] font-['Arial:Regular',sans-serif] text-[14px] focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="space-y-1">
                <label className="font-['Arial:Regular',sans-serif] text-[12px] leading-[16px] text-[#4a5565]">
                  To
                </label>
                <input
                  type="date"
                  value={reportPeriodTo}
                  onChange={(e) => setReportPeriodTo(e.target.value)}
                  className="w-full h-[37.6px] px-3 border border-[#d1d5dc] rounded-[10px] font-['Arial:Regular',sans-serif] text-[14px] focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Include Sections */}
          <div className="space-y-2">
            <label className="font-['Arial:Regular',sans-serif] text-[14px] leading-[20px] text-neutral-950">
              Include Sections
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeSections.executiveSummary}
                  onChange={(e) => setIncludeSections({ ...includeSections, executiveSummary: e.target.checked })}
                  className="w-[13px] h-[13px]"
                />
                <span className="font-['Arial:Regular',sans-serif] text-[14px] leading-[20px] text-neutral-950">
                  Executive Summary
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeSections.performanceMetrics}
                  onChange={(e) => setIncludeSections({ ...includeSections, performanceMetrics: e.target.checked })}
                  className="w-[13px] h-[13px]"
                />
                <span className="font-['Arial:Regular',sans-serif] text-[14px] leading-[20px] text-neutral-950">
                  Performance Metrics
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeSections.riskAnalysis}
                  onChange={(e) => setIncludeSections({ ...includeSections, riskAnalysis: e.target.checked })}
                  className="w-[13px] h-[13px]"
                />
                <span className="font-['Arial:Regular',sans-serif] text-[14px] leading-[20px] text-neutral-950">
                  Risk Analysis
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeSections.financialOverview}
                  onChange={(e) => setIncludeSections({ ...includeSections, financialOverview: e.target.checked })}
                  className="w-[13px] h-[13px]"
                />
                <span className="font-['Arial:Regular',sans-serif] text-[14px] leading-[20px] text-neutral-950">
                  Financial Overview
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeSections.keyMilestones}
                  onChange={(e) => setIncludeSections({ ...includeSections, keyMilestones: e.target.checked })}
                  className="w-[13px] h-[13px]"
                />
                <span className="font-['Arial:Regular',sans-serif] text-[14px] leading-[20px] text-neutral-950">
                  Key Milestones
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeSections.recommendations}
                  onChange={(e) => setIncludeSections({ ...includeSections, recommendations: e.target.checked })}
                  className="w-[13px] h-[13px]"
                />
                <span className="font-['Arial:Regular',sans-serif] text-[14px] leading-[20px] text-neutral-950">
                  Recommendations
                </span>
              </label>
            </div>
          </div>

          {/* Filter By */}
          <div className="space-y-2">
            <label className="font-['Arial:Regular',sans-serif] text-[14px] leading-[20px] text-neutral-950">
              Filter by
            </label>
            <select className="w-full h-[37.6px] px-3 border border-[#d1d5dc] rounded-[10px] font-['Arial:Regular',sans-serif] text-[14px] focus:outline-none focus:border-blue-500 bg-white">
              <option>All Projects</option>
              <option>High Priority Only</option>
              <option>At Risk Projects</option>
              <option>By Business Unit</option>
              <option>By Sponsor</option>
            </select>
          </div>

          {/* Format */}
          <div className="space-y-2">
            <label className="font-['Arial:Regular',sans-serif] text-[14px] leading-[20px] text-neutral-950">
              Export format
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedFormat('csv')}
                className={`flex-1 h-[37.6px] rounded-[10px] font-['Arial:Regular',sans-serif] text-[14px] transition-colors ${
                  selectedFormat === 'csv'
                    ? 'bg-[#4f39f6] text-white'
                    : 'border border-[#d1d5dc] text-neutral-950 hover:bg-gray-50'
                }`}
              >
                CSV
              </button>
              <button
                onClick={() => setSelectedFormat('pdf')}
                className={`flex-1 h-[37.6px] rounded-[10px] font-['Arial:Regular',sans-serif] text-[14px] transition-colors ${
                  selectedFormat === 'pdf'
                    ? 'bg-[#4f39f6] text-white'
                    : 'border border-[#d1d5dc] text-neutral-950 hover:bg-gray-50'
                }`}
              >
                PDF
              </button>
              <button
                onClick={() => setSelectedFormat('excel')}
                className={`flex-1 h-[37.6px] rounded-[10px] font-['Arial:Regular',sans-serif] text-[14px] transition-colors ${
                  selectedFormat === 'excel'
                    ? 'bg-[#4f39f6] text-white'
                    : 'border border-[#d1d5dc] text-neutral-950 hover:bg-gray-50'
                }`}
              >
                Excel
              </button>
              <button
                onClick={() => setSelectedFormat('ppt')}
                className={`flex-1 h-[37.6px] rounded-[10px] font-['Arial:Regular',sans-serif] text-[14px] transition-colors ${
                  selectedFormat === 'ppt'
                    ? 'bg-[#4f39f6] text-white'
                    : 'border border-[#d1d5dc] text-neutral-950 hover:bg-gray-50'
                }`}
              >
                PPT
              </button>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={!projectId || generating}
            className="w-full h-[44px] bg-[#4f39f6] text-white rounded-[10px] font-['Arial:Regular',sans-serif] text-[16px] hover:bg-[#432dd7] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {generating ? 'Generating...' : 'Generate Report'}
          </button>

          {/* Quick Actions */}
          <div className="pt-4 border-t border-gray-200 space-y-2">
            <p className="font-['Arial:Regular',sans-serif] text-[12px] leading-[16px] text-[#4a5565]">
              Quick Actions
            </p>
            <button
              type="button"
              onClick={() => reports[0] && projectId && handleExport(reports[0].id)}
              disabled={!projectId || !reports.length}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Download Last Generated Report
            </button>
            {/*
              Sprint 20 — "Schedule Recurring Report" used to be a no-op
              button. The real scheduled-reports UI lives under Admin
              Settings → Approval Workflows (ScheduledReportsManager).
              Sending the user there is more honest than ignoring the
              click; once Admin gets a deep-link by section we can
              point at the exact panel.
            */}
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              Schedule Recurring Report
            </button>
            {/*
              Sprint 20 — dropped "View Report History". The
              "Recently Generated Reports" panel rendered immediately
              below IS the report history; a second button with the
              same target was confusing dead weight.
            */}
          </div>
        </div>
      </div>

      {/* Recent Reports */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-['Arial:Regular',sans-serif] text-[16px] leading-[24px] text-neutral-950 mb-4">
          Recently Generated Reports
        </h3>
        {reportsLoading ? (
          <div className="py-8 text-center text-gray-500">Loading reports...</div>
        ) : projectId && reports.length === 0 ? (
          <div className="py-8 text-center text-gray-500">No reports yet. Generate one above.</div>
        ) : !projectId ? (
          <div className="py-8 text-center text-gray-500">Open a project to see reports.</div>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => (
              <div
                key={report.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-['Arial:Regular',sans-serif] text-[14px] text-neutral-950">
                      {report.title}
                    </p>
                    <p className="font-['Arial:Regular',sans-serif] text-[12px] text-[#4a5565]">
                      {report.generated_at ? new Date(report.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} • {report.report_type}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleExport(report.id)}
                  className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}